import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {Split, withShade} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CompanyAskGlyph} from 'components/DSYS/CompanyOSLogo';
import {MouseEvent, ReactNode} from 'react';

import styles from './SearchBox.module.css';

/**
 * The box's few type and row rules, in one place:
 * - One bold per view: the panel's subject. Everything you can pick is
 *   regular text; supporting facts are `subtle`; section labels are `Label`.
 * - Ask always looks the same: an `AskRow` (glyph + question). Chips are only
 *   topics.
 * - Rows pad by 8px and a `RowList` pulls them left by the same, so text
 *   lines up with the labels above.
 */

export const Label = ({children}: {children: ReactNode}) => (
  <Text size="small" color="faint" isBold tagName="div">
    {children}
  </Text>
);

/** Supporting text: roles, times, short names. */
export const Meta = ({children}: {children: ReactNode}) => (
  <Text size="small" color="subtle" tagName="div">
    <Truncate>{children}</Truncate>
  </Text>
);

export const RowList = ({children}: {children: ReactNode}) => (
  <div className={styles.rowList}>{children}</div>
);

/** Anything pickable as a full-width row: shades on hover, no outline. */
export const Row = ({
  children,
  onClick,
  onMouseOver,
}: {
  children: ReactNode;
  onClick?: () => void;
  onMouseOver?: (event: MouseEvent) => void;
}) => (
  <div
    {...withShade({className: `${styles.row}${onClick ? ` ${styles.rowClickable}` : ''}`})}
    onClick={onClick}
    onMouseOver={onMouseOver}
  >
    {children}
  </div>
);

/** An Ask question as a row: the bare Ask glyph (no circle), then the question in regular text. */
export const AskRow = ({
  label,
  onClick,
  onMouseOver,
}: {
  label: string;
  onClick: () => void;
  onMouseOver?: (event: MouseEvent) => void;
}) => (
  <Row onClick={onClick} onMouseOver={onMouseOver}>
    <Split gap="8" alignY="center">
      <Split.Item>
        <UIIcon src={CompanyAskGlyph} />
      </Split.Item>
      <Split.Item width="fill" style={{minWidth: 0}}>
        <Text tagName="div">
          <Truncate>{label}</Truncate>
        </Text>
      </Split.Item>
    </Split>
  </Row>
);
